// AG-02 — Panel d'analyse IA du DAL (Diagnostic Autonomie Logement)
// Appelle POST /api/metier/agents/dal/run et affiche verdict + suggestions.
import { useState } from "react";
import {
  Sparkles, Loader2, AlertTriangle, CheckCircle2, AlertCircle,
  XCircle, Brain, ChevronDown, ChevronRight, FileWarning
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import AgentMarkdown from "../agents/AgentMarkdown";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL || "";

const VERDICT_STYLES = {
  COMPLET: { bg: "bg-[var(--oh-success-bg)]", text: "text-[var(--oh-success-text)]", border: "border-[var(--oh-success-border)]", icon: CheckCircle2 },
  "À COMPLÉTER": { bg: "bg-[var(--oh-info-bg)]", text: "text-[var(--oh-info-text)]", border: "border-[var(--oh-info-border)]", icon: AlertCircle },
  INCOMPLET: { bg: "bg-[var(--oh-info-bg)]", text: "text-[var(--oh-info-text)]", border: "border-[var(--oh-info-border)]", icon: AlertCircle },
  INCOHÉRENT: { bg: "bg-[var(--oh-info-bg)]", text: "text-[var(--oh-info-text)]", border: "border-[var(--oh-info-border)]", icon: AlertTriangle },
  BLOQUÉ: { bg: "bg-[var(--oh-error-bg)]", text: "text-[var(--oh-error-text)]", border: "border-[var(--oh-error-border)]", icon: XCircle },
  INCONNU: { bg: "bg-zinc-500/15", text: "text-[var(--oh-text-secondary)]", border: "border-[var(--oh-border)]", icon: AlertCircle },
};

export function DALAgentPanel({ reference, tenantId, onRedige, redigeLoading = false }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const runAnalysis = async () => {
    if (!reference) {
      toast.error("Référence dossier manquante");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/agents/dal/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...enteteTenant(tenantId),
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ reference }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        toast.success(`Analyse terminée — Verdict : ${data.verdict}`);
      } else {
        toast.error(data.detail || "Erreur lors de l'analyse");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  const verdict = result?.verdict || "INCONNU";
  const style = VERDICT_STYLES[verdict] || VERDICT_STYLES.INCONNU;
  const VerdictIcon = style.icon;

  const completion = result?.completion;
  const incoherences = result?.incoherences;
  const plans = result?.plans;

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{
        background: "var(--oh-bg-elevated)",
        border: "1px solid var(--oh-border)",
      }}
      data-testid="dal-agent-panel"
    >
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid var(--oh-border)" }}
      >
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4" style={{ color: "var(--oh-hub-conformite)" }} />
          <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
            Analyse IA du DAL
          </span>
          {result && (
            <Badge className={`${style.bg} ${style.text} ${style.border} border ml-2`}>
              <VerdictIcon className="w-3 h-3 mr-1" />
              {verdict}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onRedige && (
            <Button
              size="sm"
              onClick={onRedige}
              disabled={redigeLoading}
              className="h-7 px-3 text-xs"
              style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}
              data-testid="btn-redige-dal"
            >
              {redigeLoading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Rédaction…
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                  Rédiger (IA)
                </>
              )}
            </Button>
          )}
          <Button
            size="sm"
            onClick={runAnalysis}
            disabled={loading || !reference}
            className="h-7 px-3 text-xs"
            data-testid="btn-run-dal-agent"
          >
            {loading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                Audit en cours…
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                {result ? "Relancer l'audit" : "Auditer le DAL"}
              </>
            )}
          </Button>
        </div>
      </div>

      {!result && !loading && (
        <div className="p-4 text-sm" style={{ color: "var(--oh-text-muted)" }}>
          Lance l'agent IA pour vérifier la complétude du DAL, détecter les
          incohérences besoins/préconisations et auditer les plans 2D/3D.
          {!reference && (
            <span className="block mt-1 text-[var(--oh-info-text)]">
              ⚠ La référence du dossier doit être disponible.
            </span>
          )}
        </div>
      )}

      {result && (
        <div className="p-4 space-y-4">
          {/* Score complétude */}
          {completion && (
            <div
              className="p-3 rounded"
              style={{
                background: "var(--oh-bg-surface)",
                border: "1px solid var(--oh-border)",
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>
                  Complétude
                </span>
                <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
                  {completion.score_pct}% ({completion.weight_done}/{completion.weight_total})
                </span>
              </div>
              <div className="h-2 rounded overflow-hidden" style={{ background: "var(--oh-bg-surface)" }}>
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${completion.score_pct}%`,
                    background:
                      completion.score_pct >= 80
                        ? "rgb(16,185,129)"
                        : completion.score_pct >= 50
                        ? "rgb(245,158,11)"
                        : "rgb(239,68,68)",
                  }}
                />
              </div>
              {completion.missing_fields?.length > 0 && (
                <details className="mt-3">
                  <summary
                    className="text-xs cursor-pointer flex items-center gap-1"
                    style={{ color: "var(--oh-text-muted)" }}
                  >
                    <ChevronRight className="w-3 h-3" />
                    {completion.missing_fields.length} champ(s) manquant(s)
                  </summary>
                  <ul className="mt-2 space-y-1 ml-4">
                    {completion.missing_fields.map((m) => (
                      <li
                        key={m.field}
                        className="text-xs"
                        style={{ color: "var(--oh-text-muted)" }}
                      >
                        <span
                          className="font-mono mr-1"
                          style={{
                            color:
                              m.weight >= 3 ? "rgb(239,68,68)" : "var(--oh-text-muted)",
                          }}
                        >
                          [{m.weight}]
                        </span>
                        {m.label}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* Incohérences */}
          {incoherences && incoherences.issues?.length > 0 && (
            <div
              className="p-3 rounded"
              style={{
                background: "var(--oh-bg-surface)",
                border: "1px solid var(--oh-border)",
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <FileWarning className="w-4 h-4 text-[var(--oh-info-text)]" />
                <span className="text-xs uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>
                  Incohérences ({incoherences.errors_count} erreurs / {incoherences.warnings_count} warnings)
                </span>
              </div>
              <ul className="space-y-2">
                {incoherences.issues.map((i, idx) => (
                  <li key={idx} className="text-xs flex items-start gap-2">
                    <span
                      className={
                        i.severity === "error"
                          ? "text-[var(--oh-error-text)]"
                          : "text-[var(--oh-info-text)]"
                      }
                    >
                      {i.severity === "error" ? "✗" : "⚠"}
                    </span>
                    <div className="flex-1">
                      <p style={{ color: "var(--oh-text)" }}>{i.message}</p>
                      {i.suggestion && (
                        <p className="mt-0.5 italic" style={{ color: "var(--oh-text-muted)" }}>
                          → {i.suggestion}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Plans / photos (Phase B) */}
          {plans && (
            <div
              className="p-3 rounded"
              style={{
                background: "var(--oh-bg-surface)",
                border: "1px solid var(--oh-border)",
              }}
            >
              <span
                className="text-xs uppercase tracking-wide block mb-2"
                style={{ color: "var(--oh-text-muted)" }}
              >
                Plans &amp; photos
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {plans.plans_status?.map((p) => (
                  <div
                    key={p.type}
                    className="p-2 rounded text-xs"
                    style={{
                      background: "var(--oh-bg-surface)",
                      border: `1px solid ${p.present ? "rgba(16,185,129,0.3)" : "rgba(245,158,11,0.3)"}`,
                    }}
                  >
                    <div style={{ color: "var(--oh-text-muted)" }}>{p.type}</div>
                    <div
                      className="font-medium"
                      style={{ color: p.present ? "rgb(16,185,129)" : "rgb(245,158,11)" }}
                    >
                      {p.present
                        ? p.count != null
                          ? `${p.count} ✓`
                          : "✓"
                        : "absent"}
                    </div>
                  </div>
                ))}
              </div>
              {plans.issues?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {plans.issues.map((i, idx) => (
                    <li
                      key={idx}
                      className="text-xs"
                      style={{ color: "var(--oh-text-muted)" }}
                    >
                      <span
                        className={
                          i.severity === "error" ? "text-[var(--oh-error-text)]" : "text-[var(--oh-info-text)]"
                        }
                      >
                        {i.severity === "error" ? "✗" : "⚠"}
                      </span>{" "}
                      {i.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Rapport markdown */}
          <details
            open={!completion}
            onToggle={(e) => setShowRaw(e.currentTarget.open)}
          >
            <summary
              className="text-xs cursor-pointer flex items-center gap-1"
              style={{ color: "var(--oh-text-muted)" }}
            >
              {showRaw ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
              Rapport complet de l'agent ({result.iterations} itérations,{" "}
              {Math.round(result.duration_ms / 1000)}s)
            </summary>
            <div
              className="mt-2 p-3 rounded"
              style={{
                background: "var(--oh-bg-surface)",
                border: "1px solid var(--oh-border)",
                color: "var(--oh-text)",
                maxHeight: "400px",
                overflow: "auto",
              }}
              data-testid="dal-agent-analysis"
            >
              <AgentMarkdown density="compact">{result.analysis}</AgentMarkdown>
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
